import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FONT } from "../lib/tokens";
import { computeRecipeMatch } from "../lib/recipePairing";
import { recipeAllergyConflicts } from "../data/allergens";
import RecipeStamp from "./RecipeStamp";

// ── Index / TOC page ────────────────────────────────────────────────

// "Recently added" threshold — anything created within this window
// gets the teal NEW pill in the TOC. 7 days is short enough to feel
// "new" rather than "ever-present" but long enough that infrequent
// users still see the affordance after a save.
const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const FILTER_DEFS = [
  { id: "all",      label: "All",         test: () => true },
  { id: "favorite", label: "★ Favorites", test: (r) => r.isFavorite === true },
  { id: "ai",       label: "AI",          test: (r) => r.source === "ai" },
  // "Human" (rather than "Custom") — paired with the "Made with
  // Love" mama mascot. Tests against source === "custom" still
  // because that's the DB value for user-authored rows; the chip
  // label is just the editorial framing.
  { id: "human",    label: "Human",       test: (r) => r.source === "custom" },
  { id: "recent",   label: "Recent",      test: (r) => isRecentlyAdded(r) },
];

function isRecentlyAdded(r) {
  const t = r?.createdAt ? +new Date(r.createdAt) : NaN;
  return Number.isFinite(t) && Date.now() - t < RECENT_WINDOW_MS;
}

// Sort definitions for the TOC. Each comparator is given (a, b) of
// the same shape: { recipe, originalIndex, stats }. Stable sort —
// preserve creation order on ties.
const SORT_DEFS = [
  { id: "newest", label: "Newest", test: (a, b) => (+new Date(b.recipe.createdAt || 0)) - (+new Date(a.recipe.createdAt || 0)) },
  { id: "oldest", label: "Oldest", test: (a, b) => (+new Date(a.recipe.createdAt || 0)) - (+new Date(b.recipe.createdAt || 0)) },
  { id: "az",     label: "A–Z",    test: (a, b) => String(a.recipe.recipe?.title || "").toLowerCase().localeCompare(String(b.recipe.recipe?.title || "").toLowerCase()) },
  { id: "cooked", label: "Most cooked",  test: (a, b) => (b.stats?.cookCount || 0) - (a.stats?.cookCount || 0) },
  { id: "last",   label: "Last cooked",  test: (a, b) => (b.stats?.lastCookedAt || 0) - (a.stats?.lastCookedAt || 0) },
];

// Substring + token-tolerant search match. Empty query returns all.
function matchesQuery(r, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const rec = r.recipe || {};
  const haystack = [
    rec.title || "",
    rec.subtitle || "",
    rec.cuisine || "",
    ...(Array.isArray(rec.ingredients) ? rec.ingredients.map((i) => i?.item || i?.name || "") : []),
    // Tags — both the filterable column (r.tags) and any recipe-jsonb
    // tags — so typing "fancy" / "date-night" finds intent-tagged recipes.
    ...(Array.isArray(r.tags) ? r.tags : []),
    ...(Array.isArray(rec.tags) ? rec.tags : []),
  ].join(" ").toLowerCase();
  return haystack.includes(q);
}

// ── Chip styling ────────────────────────────────────────────────────
// Shared by the filter row and the refine panels. 12px floor
// everywhere — the old 8-9px chip zone is a big part of what made
// this page unreadable once the tag vocabulary grew.
const CHIP_BASE = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", borderRadius: 999,
  fontFamily: FONT.forma, fontSize: 12,
  cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
};

const optionChipStyle = (active) => ({
  ...CHIP_BASE,
  padding: "5px 11px", fontWeight: 500,
  background: active ? "#2F8F83" : "rgba(124,94,44,0.06)",
  border: `1px solid ${active ? "#26766B" : "rgba(124,94,44,0.25)"}`,
  color: active ? "#FFFFFF" : "#5C3F18",
});

// Single-line horizontally-scrollable chip row. The page it lives on
// is a framer-motion drag="x" surface (the page-turn gesture), so the
// row must (a) declare touchAction pan-x for native touch scrolling
// and (b) stop pointerdown from reaching the page's drag handler —
// otherwise a swipe on the chips turns the page instead of scrolling
// the row. Bleeds to the page edges so scrolling reads as intended.
function ChipScrollRow({ children, style }) {
  return (
    <div
      onPointerDownCapture={(e) => e.stopPropagation()}
      style={{
        display: "flex", gap: 6,
        overflowX: "auto", flexWrap: "nowrap",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x",
        margin: "0 -30px", padding: "2px 30px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export default function IndexPage({ recipes, cookStats, pantry = [], onJumpTo, viewerAllergies = [] }) {
  const [filterId, setFilterId] = useState("all");
  const [sortId, setSortId] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  // Which refine panel is open: null | "sort" | "cuisine" | "tags".
  // One at a time, and picking an option closes it — the results
  // list gets the page back the moment a choice lands.
  const [refineOpen, setRefineOpen] = useState(null);
  const filterDef = FILTER_DEFS.find((f) => f.id === filterId) || FILTER_DEFS[0];
  const sortDef = SORT_DEFS.find((s) => s.id === sortId) || SORT_DEFS[0];

  // Pre-compute pantry-match summary per recipe. Memoized on pantry +
  // recipes so we don't re-pair on every render. The badge column in
  // the list below reads matchBySlug[r.slug] to render READY or the
  // "N missing" count. ingredientInfo is intentionally omitted here
  // (it's an optional sub-tier signal) — the basic pair walk is
  // enough for the badge.
  const matchBySlug = useMemo(() => {
    const map = new Map();
    for (const r of recipes) {
      const rec = r?.recipe;
      if (!rec || !rec.slug) continue;
      try {
        map.set(rec.slug, computeRecipeMatch(rec, pantry));
      } catch (_) { /* skip recipe on pairing crash */ }
    }
    return map;
  }, [recipes, pantry]);

  // SAFETY chips — allergen conflicts per recipe (warn, don't hide).
  // Keyed by row id; empty map when the viewer has no allergies.
  const allergyBySlug = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(viewerAllergies) || viewerAllergies.length === 0) return map;
    for (const r of recipes) {
      const hits = recipeAllergyConflicts(r?.recipe, viewerAllergies);
      if (hits.length > 0) map.set(r.id, hits);
    }
    return map;
  }, [recipes, viewerAllergies]);

  // Distinct cuisines in the user's book, with per-cuisine counts
  // for the refine panel. Need 2+ to be worth offering as a facet.
  const cuisineCounts = {};
  for (const r of recipes) {
    const c = (r.recipe?.cuisine || "").toLowerCase();
    if (c) cuisineCounts[c] = (cuisineCounts[c] || 0) + 1;
  }
  const cuisines = Object.keys(cuisineCounts).sort();

  // Distinct tags across the user's book, most-used first. The tag
  // vocabulary is long-tail (mostly one-use descriptors), so it lives
  // behind the TAGS refine chip as a browsable, bounded panel — never
  // as an always-open chip wall that buries the recipe list.
  const tagCounts = {};
  for (const r of recipes) {
    for (const t of (Array.isArray(r.tags) ? r.tags : [])) {
      if (t) tagCounts[t] = (tagCounts[t] || 0) + 1;
    }
  }
  const tagsAll = Object.keys(tagCounts).sort(
    (a, b) => (tagCounts[b] - tagCounts[a]) || a.localeCompare(b),
  );

  // Filter the list but preserve original index — when the user taps
  // a row we still need to jump to its real page (2 + originalIndex),
  // not its sorted/filtered index, otherwise jumping would land them
  // on the wrong recipe.
  const filteredWithIndex = recipes
    .map((r, i) => ({
      recipe: r,
      originalIndex: i,
      stats: cookStats?.get(r.slug) || null,
    }))
    .filter(({ recipe: r }) => filterDef.test(r))
    .filter(({ recipe: r }) => !cuisineFilter || (r.recipe?.cuisine || "").toLowerCase() === cuisineFilter)
    .filter(({ recipe: r }) => !tagFilter || (Array.isArray(r.tags) && r.tags.includes(tagFilter)))
    .filter(({ recipe: r }) => matchesQuery(r, searchQuery))
    .sort(sortDef.test);

  // Anything narrowing the list right now? Drives the "Showing N of
  // M · Clear all" strip so a stray tag/cuisine pick can never
  // silently strand the user on a one-row book.
  const refined = filterId !== "all" || !!cuisineFilter || !!tagFilter || !!searchQuery.trim();
  const clearRefinements = () => {
    setFilterId("all");
    setCuisineFilter(null);
    setTagFilter(null);
    setSearchQuery("");
    setRefineOpen(null);
  };

  // Refine bar model — one summary chip per collapsed facet. Sort is
  // always present; cuisine/tags only when the book has enough
  // vocabulary to filter on.
  const refineChips = [
    {
      id: "sort", label: "Sort", value: sortDef.label,
      engaged: sortId !== "newest",
      onClear: sortId !== "newest" ? () => setSortId("newest") : null,
    },
    cuisines.length >= 2 && {
      id: "cuisine", label: "Cuisine", value: cuisineFilter || "Any",
      engaged: !!cuisineFilter,
      onClear: cuisineFilter ? () => setCuisineFilter(null) : null,
    },
    tagsAll.length > 0 && {
      id: "tags", label: "Tags", value: tagFilter || "Any",
      engaged: !!tagFilter,
      onClear: tagFilter ? () => setTagFilter(null) : null,
    },
  ].filter(Boolean);

  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      padding: "44px 30px 24px",
      // Anchor the AI mascot's absolute positioning to the page,
      // not the parent overlay (which is also position:absolute).
      position: "relative",
    }}>
      {/* Filter mascots — SLAP into top-right whenever the matching
          filter is active. AI gets Chadwick, HUMAN gets the "Made
          with Love" mama. Same animation + position; they swap by
          which filter is selected. RecipeStamp must be the DIRECT
          child of AnimatePresence (no wrapper div) for the exit
          animation to fire. Position via style so the motion.img
          inside owns the absolute coords. */}
      <AnimatePresence>
        {filterId === "ai" && (
          <RecipeStamp
            key="stamp-ai"
            src="/img/ai-recipe-book-filter.webp"
            size={92}
            style={{
              position: "absolute",
              top: 14, right: 36,
              zIndex: 2,
            }}
          />
        )}
        {filterId === "human" && (
          <RecipeStamp
            key="stamp-human"
            src="/img/human-recipe-book-filter.webp"
            size={92}
            style={{
              position: "absolute",
              top: 14, right: 36,
              zIndex: 2,
            }}
          />
        )}
      </AnimatePresence>
      <div style={{
        fontFamily: FONT.forma, fontSize: 12, color: "#9C7E40",
        letterSpacing: "0.20em",
      }}>
        CONTENTS
      </div>
      <div style={{
        marginTop: 10, fontFamily: FONT.forma, fontSize: 22,
        fontWeight: 600, color: "#3A2A12", lineHeight: 1.15,
        letterSpacing: "-0.01em",
      }}>
        What's in here
      </div>

      {/* Search input — substring match across title, cuisine,
          ingredients, subtitle, tags. Pure client-side filter; the
          book is small enough that scanning is instant. */}
      <div style={{ marginTop: 16, position: "relative" }}>
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search your book…"
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "10px 36px 10px 14px",
            background: "rgba(255,250,236,0.6)",
            border: "1px solid rgba(124,94,44,0.30)",
            borderRadius: 8,
            fontFamily: FONT.forma, fontSize: 13,
            color: "#3A2A12",
            outline: "none",
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            aria-label="Clear search"
            style={{
              position: "absolute", right: 8, top: "50%",
              transform: "translateY(-50%)",
              width: 24, height: 24, borderRadius: 12,
              background: "rgba(124,94,44,0.15)",
              border: "1px solid rgba(124,94,44,0.30)",
              color: "#5C3F18", fontSize: 12, lineHeight: 1,
              cursor: "pointer", padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Primary filter chips — ONE line, horizontally scrollable,
          never wraps. Active chip uses the project teal so it ties
          visually to the cover and to the NEW pill. */}
      <ChipScrollRow style={{ marginTop: 12 }}>
        {FILTER_DEFS.map((f) => {
          // Hide filters that would yield zero results — keeps the
          // chip row from offering dead-end taps. ALL stays even
          // when the book is empty so the row never disappears.
          const count = f.id === "all"
            ? recipes.length
            : recipes.filter(f.test).length;
          if (count === 0 && f.id !== "all") return null;
          const active = filterId === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilterId(f.id)}
              style={{
                ...CHIP_BASE,
                gap: 5, fontWeight: 600,
                background: active ? "#2F8F83" : "rgba(124,94,44,0.08)",
                border: `1px solid ${active ? "#26766B" : "rgba(124,94,44,0.30)"}`,
                color: active ? "#FFFFFF" : "#5C3F18",
              }}
            >
              {f.label}
              <span style={{
                fontSize: 12, fontWeight: 500,
                opacity: active ? 0.75 : 0.55,
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </ChipScrollRow>

      {/* Refine bar — sort / cuisine / tags each collapse into one
          summary chip ("Tags · quick ×"). Tapping a chip opens ONE
          bounded panel below it. This replaces three always-open
          wrapping chip rows that, once the tag vocabulary grew past
          ~20, buried the recipe list under a wall of chips and left
          a single visible result row. */}
      <ChipScrollRow style={{ marginTop: 8 }}>
        {refineChips.map((c) => {
          const open = refineOpen === c.id;
          // Two-segment pill: the body toggles the panel, the ×
          // (when a non-default value is set) resets the facet. Two
          // SIBLING native buttons — a nested role="button" span was
          // an 18px target with no keyboard path, and a near-miss
          // toggled the panel instead of clearing.
          return (
            <div
              key={c.id}
              style={{
                display: "inline-flex", alignItems: "stretch",
                borderRadius: 999, flexShrink: 0, overflow: "hidden",
                background: c.engaged
                  ? "rgba(195,144,35,0.16)"
                  : open ? "rgba(124,94,44,0.14)" : "rgba(124,94,44,0.05)",
                border: `1px solid ${c.engaged ? "rgba(195,144,35,0.60)" : "rgba(124,94,44,0.25)"}`,
                color: c.engaged ? "#7A5A18" : "#5C3F18",
              }}
            >
              <button
                onClick={() => setRefineOpen(open ? null : c.id)}
                aria-expanded={open}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: c.onClear ? "6px 8px 6px 12px" : "6px 12px",
                  background: "transparent", border: "none",
                  fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
                  color: "inherit", cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                <span style={{ opacity: 0.65 }}>{c.label} ·</span>
                <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{c.value}</span>
                {!c.onClear && (
                  <span aria-hidden style={{ fontSize: 12, opacity: 0.5 }}>
                    {open ? "▴" : "▾"}
                  </span>
                )}
              </button>
              {c.onClear && (
                <button
                  onClick={() => { c.onClear(); setRefineOpen(null); }}
                  aria-label={`Reset ${c.label.toLowerCase()}`}
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    minWidth: 30, padding: "6px 10px 6px 8px",
                    background: "transparent", border: "none",
                    borderLeft: "1px solid rgba(124,94,44,0.22)",
                    color: "inherit", fontSize: 14, lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </ChipScrollRow>

      {/* Refine panel — the one place the full facet vocabulary
          renders. Bounded height with its own scroll, so the tag
          study stays browsable (counts and all) without ever eating
          the results list. */}
      {refineOpen && (
        <div style={{
          marginTop: 8, padding: 10,
          background: "rgba(255,250,236,0.55)",
          border: "1px solid rgba(124,94,44,0.22)",
          borderRadius: 10,
          maxHeight: 132, overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {refineOpen === "sort" && SORT_DEFS.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSortId(s.id); setRefineOpen(null); }}
                style={optionChipStyle(sortId === s.id)}
              >
                {s.label}
              </button>
            ))}
            {refineOpen === "cuisine" && (
              <>
                <button
                  onClick={() => { setCuisineFilter(null); setRefineOpen(null); }}
                  style={optionChipStyle(!cuisineFilter)}
                >
                  Any
                </button>
                {cuisines.map((c) => {
                  const active = cuisineFilter === c;
                  return (
                    <button
                      key={c}
                      onClick={() => { setCuisineFilter(active ? null : c); setRefineOpen(null); }}
                      style={{ ...optionChipStyle(active), textTransform: "capitalize" }}
                    >
                      {c}
                      <span style={{ fontSize: 12, opacity: active ? 0.75 : 0.55 }}>
                        {cuisineCounts[c]}
                      </span>
                    </button>
                  );
                })}
              </>
            )}
            {refineOpen === "tags" && (
              <>
                <button
                  onClick={() => { setTagFilter(null); setRefineOpen(null); }}
                  style={optionChipStyle(!tagFilter)}
                >
                  Any
                </button>
                {tagsAll.map((t) => {
                  const active = tagFilter === t;
                  return (
                    <button
                      key={t}
                      onClick={() => { setTagFilter(active ? null : t); setRefineOpen(null); }}
                      style={optionChipStyle(active)}
                    >
                      {t}
                      <span style={{ fontSize: 12, opacity: active ? 0.75 : 0.55 }}>
                        {tagCounts[t]}
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* Result strip — whenever anything is narrowing the list, say
          so out loud and offer the exit. A stray tag pick used to be
          invisible; the user just saw a mysteriously tiny book. */}
      {refined && (
        <div style={{
          marginTop: 10, display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 8,
        }}>
          <span style={{ fontFamily: FONT.forma, fontSize: 12, color: "#7C5E2C" }}>
            Showing {filteredWithIndex.length} of {recipes.length}
          </span>
          <button
            onClick={clearRefinements}
            style={{
              // Padding + negative margin: ~32px hit target without
              // shifting the strip's visual layout. This is the rescue
              // affordance for "why is my book one row" — it has to be
              // easy to hit.
              background: "none", border: "none",
              padding: "10px 8px", margin: "-10px -8px",
              fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              color: "#2F8F83", cursor: "pointer",
              textDecoration: "underline", textUnderlineOffset: 2,
            }}
          >
            Clear all
          </button>
        </div>
      )}

      <div style={{
        marginTop: 14, flex: 1, overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        display: "flex", flexDirection: "column", gap: 2,
      }}>
        <style>{`@keyframes rbGenPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
        {filteredWithIndex.length === 0 ? (
          <div style={{
            marginTop: 28, fontFamily: FONT.forma, fontSize: 13,
            fontStyle: "italic", color: "#7C5E2C", textAlign: "center",
          }}>
            Nothing matches that filter yet.
          </div>
        ) : filteredWithIndex.map(({ recipe: r, originalIndex, stats }) => {
          const isRecent = isRecentlyAdded(r);
          const cookCount = stats?.cookCount || 0;
          // Background-generation status. A pending/failed stub shows a
          // status chip instead of pantry-match / cook badges (those are
          // meaningless on an empty stub).
          const gen = r.recipe?._gen || null;
          const match = matchBySlug.get(r.recipe?.slug || "") || null;
          // Pantry-match badge — three tiers so the user can scan:
          //   READY  (green): everything in pantry, including subs / wrong-state.
          //   1-3 MISSING (amber): a small shop trip away.
          //   short list omitted for 4+ missing (it's basically "make a shop").
          // Empty pantry (kitchen-tracking off passes pantry=[])
          // suppresses the column entirely — computeRecipeMatch's
          // total counts RECIPE ingredients, so without this guard
          // every row would read "N MISSING", which is pure noise
          // for a user who deliberately doesn't track inventory.
          const matchBadge = !match || match.total === 0 || pantry.length === 0
            ? null
            : match.ready
              ? { text: "READY", bg: "#2F8F83", fg: "#FFFFFF" }
              : match.missingCount <= 3
                ? { text: `${match.missingCount} MISSING`, bg: "#E0B871", fg: "#3A2A12" }
                : { text: `${match.missingCount} MISSING`, bg: "#C97C5D", fg: "#FFFFFF" };
          // Chips that belong on the meta line under the title. The
          // old layout put the WHOLE badge cluster on the title's
          // flex line — with a long CONTAINS chip present the title's
          // width collapsed to literally zero (a row rendered as
          // badges-only, no name) and every other title truncated to
          // a stub. A table of contents exists to show TITLES; badges
          // are metadata and wrap freely on their own line.
          const chips = [];
          if (gen === "pending") chips.push({ key: "writing", bg: "#C39023", fg: "#FFFFFF", text: "WRITING", pulse: true });
          if (gen === "failed") chips.push({ key: "failed", bg: "#C0392B", fg: "#FFFFFF", text: "FAILED" });
          if (!gen && matchBadge) chips.push({ key: "match", bg: matchBadge.bg, fg: matchBadge.fg, text: matchBadge.text });
          if (!gen && isRecent) chips.push({ key: "new", bg: "#2F8F83", fg: "#FFFFFF", text: "NEW" });
          if (allergyBySlug.has(r.id)) chips.push({
            key: "allergy", bg: "#C0392B", fg: "#FFFFFF",
            text: `CONTAINS ${String(allergyBySlug.get(r.id)[0]).toUpperCase()}`,
            title: `Contains ${allergyBySlug.get(r.id).join(", ")}`,
          });
          return (
            <button
              key={r.id}
              onClick={() => onJumpTo(2 + originalIndex)}
              style={{
                display: "flex", alignItems: "flex-start",
                gap: 10, padding: "10px 4px",
                background: "transparent", border: "none",
                borderBottom: "1px dashed rgba(124,94,44,0.22)",
                cursor: "pointer", textAlign: "left",
                fontFamily: FONT.itemTitle,
                width: "100%",
              }}
            >
              <span style={{
                fontFamily: FONT.forma, fontSize: 12, color: "#9C7E40",
                letterSpacing: "0.08em", flexShrink: 0, minWidth: 26,
                paddingTop: 3,
              }}>
                {String(originalIndex + 1).padStart(2, "0")}
              </span>
              <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
                {/* Title line — the title owns the width. Only the
                    tiny source marker (+ cook count) shares it. */}
                <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{
                    flex: 1, minWidth: 0,
                    fontSize: 16, fontStyle: "italic", color: "#3A2A12",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {r.recipe?.title || "(untitled)"}
                  </span>
                  <span style={{
                    fontFamily: FONT.forma, fontSize: 12, color: "#9C7E40",
                    letterSpacing: "0.06em", flexShrink: 0,
                    display: "inline-flex", gap: 6, alignItems: "baseline",
                  }}>
                    {/* DB value `custom` reads as "Human" in the book —
                        matches the HUMAN filter chip + the mama mascot. */}
                    {r.source === "custom" ? "HUMAN" : (r.source || "").toUpperCase()}
                    {cookCount > 0 && (
                      <span style={{ color: "#5C3F18", fontWeight: 600 }}>
                        · {cookCount}×
                      </span>
                    )}
                  </span>
                </span>
                {/* Meta line — badges wrap; a long allergen chip costs
                    a wrap, never the title. */}
                {chips.length > 0 && (
                  <span style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    {chips.map((c) => (
                      <span
                        key={c.key}
                        title={c.title}
                        style={{
                          fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
                          background: c.bg, color: c.fg,
                          letterSpacing: "0.08em", padding: "2px 7px", borderRadius: 4,
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}
                      >
                        {c.pulse && (
                          <span style={{
                            width: 6, height: 6, borderRadius: "50%", background: "#FFFFFF",
                            animation: "rbGenPulse 1s ease-in-out infinite",
                          }} />
                        )}
                        {c.text}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
